import React, { useEffect, useState, ChangeEvent } from 'react';
import './index.css';
import { Routes, Route, Link, useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';

interface DictionaryEntry {
  word: string;
  meaning: string;
  refference?: string;
}

interface BibleVerse {
  book: string;
  chapter: number;
  verse: number;
  text: string;
}

interface BibleData {
  Book: Array<{
    Chapter: Array<{
      Verse: Array<{
        Verseid: string;
        Verse: string;
      }>;
    }>;
  }>;
}

// Component for Word Details page
const WordDetailsPage: React.FC<{ data: DictionaryEntry[], onBack: () => void, bookmarks: string[], toggleBookmark: (word: string) => void }> = ({ data, onBack, bookmarks, toggleBookmark }) => {
  const { word: wordParam } = useParams<{ word: string }>();
  const [selectedVerses, setSelectedVerses] = useState<BibleVerse[]>([]);
  const [bibleData, setBibleData] = useState<{ [key: string]: any }>({});

  useEffect(() => {
    console.log('బైబిల్ డేటా లోడ్ చేయడం ప్రారంభం...');
    
    // ప్రతి బుక్‌కి సెపరేట్ JSON ఫైల్‌ని లోడ్ చేయడం
    const loadBibleData = async () => {
      try {
        const books = [
          'Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy',
          'Joshua', 'Judges', 'Ruth', '1 Samuel', '2 Samuel',
          '1 Kings', '2 Kings', '1 Chronicles', '2 Chronicles',
          'Ezra', 'Nehemiah', 'Esther', 'Job', 'Psalms', 'Proverbs',
          'Ecclesiastes', 'Song of Songs', 'Isaiah', 'Jeremiah',
          'Lamentations', 'Ezekiel', 'Daniel', 'Hosea', 'Joel',
          'Amos', 'Obadiah', 'Jonah', 'Micah', 'Nahum', 'Habakkuk',
          'Zephaniah', 'Haggai', 'Zechariah', 'Malachi', 'Matthew',
          'Mark', 'Luke', 'John', 'Acts', 'Romans', '1 Corinthians',
          '2 Corinthians', 'Galatians', 'Ephesians', 'Philippians',
          'Colossians', '1 Thessalonians', '2 Thessalonians',
          '1 Timothy', '2 Timothy', 'Titus', 'Philemon', 'Hebrews',
          'James', '1 Peter', '2 Peter', '1 John', '2 John',
          '3 John', 'Jude', 'Revelation'
        ];

        const loadedData: { [key: string]: any } = {};
        
        for (const book of books) {
          const response = await fetch(`https://raw.githubusercontent.com/aruljohn/Bible-telugu/main/${book}.json`);
          if (!response.ok) {
            console.error(`${book} లోడ్ కాలేదు:`, response.status);
            continue;
          }
          const data = await response.json();
          loadedData[book] = data;
          console.log(`${book} లోడ్ అయింది`);
        }

        console.log('అన్ని బుక్‌లు లోడ్ అయ్యాయి:', loadedData);
        setBibleData(loadedData);
      } catch (error) {
        console.error('బైబిల్ డేటా లోడ్ కాలేదు:', error);
      }
    };

    loadBibleData();
  }, []);

  const entry = data.find(item => item.word === wordParam);

  if (!entry) {
    return <div>పదం వివరాలు అందుబాటులో లేవు.</div>;
  }

  const isBookmarked = bookmarks.includes(entry.word);

  const handleReferenceClick = (reference: string) => {
    console.log('రెఫరెన్స్ క్లిక్ చేయబడింది:', reference);
    
    if (Object.keys(bibleData).length === 0) {
      console.log('బైబిల్ డేటా లేదు, వెర్స్ చూపించలేము');
      return;
    }

    // సెమికోలన్‌లతో స్ప్లిట్ చేయడం
    const references = reference.split(';').map(ref => ref.trim());
    console.log('స్ప్లిట్ రెఫరెన్స్‌లు:', references);

    const verses: BibleVerse[] = [];

    references.forEach(ref => {
      console.log('రెఫరెన్స్ ప్రాసెస్ చేస్తున్నాం:', ref);
      const match = ref.match(/(.+?)\s+(\d+):(\d+)/);
      if (!match) {
        console.log('మ్యాచ్ కాలేదు:', ref);
        return;
      }

      const [, bookName, chapter, verse] = match;
      console.log('మ్యాచ్ డేటా:', { bookName, chapter, verse });
      
      const bookKey = getBookKey(bookName);
      console.log('బుక్ కీ:', bookKey);
      
      if (!bookKey) {
        console.log('బుక్ కనుగొనబడలేదు:', bookName);
        return;
      }

      try {
        const bookData = bibleData[bookKey];
        console.log('బుక్ డేటా:', bookData);
        
        if (!bookData) {
          console.log('బుక్ డేటా కనుగొనబడలేదు:', bookName);
          return;
        }

        const chapterData = bookData.chapters.find((ch: any) => ch.chapter === chapter);
        console.log('చాప్టర్ డేటా:', chapterData);
        
        if (!chapterData) {
          console.log('చాప్టర్ డేటా కనుగొనబడలేదు:', chapter);
          return;
        }

        const verseData = chapterData.verses.find((v: any) => v.verse === verse);
        console.log('వెర్స్ డేటా:', verseData);
        
        if (!verseData) {
          console.log('వెర్స్ డేటా కనుగొనబడలేదు:', verse);
          return;
        }

        // Get the word from the current dictionary entry
        const currentWord = entry?.word || '';
        console.log('కరెంట్ వర్డ్:', currentWord);
        
        // Highlight the word in the verse text
        let highlightedText = verseData.text;
        if (currentWord) {
          const regex = new RegExp(currentWord, 'g');
          highlightedText = verseData.text.replace(regex, `<strong>${currentWord}</strong>`);
        }

        verses.push({
          book: bookName,
          chapter: parseInt(chapter),
          verse: parseInt(verse),
          text: highlightedText
        });
        console.log('వెర్స్ జోడించబడింది:', highlightedText);
      } catch (error) {
        console.log('వెర్స్ కనుగొనబడలేదు:', { bookName, chapter, verse, error });
      }
    });

    console.log('అన్ని వెర్స్‌లు:', verses);
    if (verses.length > 0) {
      setSelectedVerses(verses);
    } else {
      console.log('ఏ వెర్స్‌లు కనుగొనబడలేదు');
    }
  };

  // Helper function to get book key
  const getBookKey = (bookName: string): string | null => {
    console.log('బుక్ నేమ్:', bookName);
    const bookMap: { [key: string]: string } = {
      'ఆది': 'Genesis',
      'నిర్గమ': 'Exodus',
      'లేవీ': 'Leviticus',
      'సంఖ్యా': 'Numbers',
      'సంఖ్య': 'Numbers',
      'ధర్మ': 'Deuteronomy',
      'ద్వితీయో': 'Deuteronomy',
      'యెహోషువ': 'Joshua',
      'యెహో': 'Joshua',
      'న్యాయాధిపతులు': 'Judges',
      'న్యాయాధి': 'Judges',
      'రూతు': 'Ruth',
      '1సమూయేలు': '1 Samuel',
      'I సమూయేలు': '1 Samuel',
      'I సమూ': '1 Samuel',
      '2సమూయేలు': '2 Samuel',
      'II సమూయేలు': '2 Samuel',
      'II సమూ': '2 Samuel',
      '1రాజులు': '1 Kings',
      'I రాజులు': '1 Kings',
      'I రాజు': '1 Kings',
      '2రాజులు': '2 Kings',
      'II రాజులు': '2 Kings',
      'II రాజు': '2 Kings',
      '1దినవృత్తాంతములు': '1 Chronicles',
      'I దినవృత్తాంతములు': '1 Chronicles',
      'I దిన': '1 Chronicles',
      '2దినవృత్తాంతములు': '2 Chronicles',
      'II దినవృత్తాంతములు': '2 Chronicles',
      'II దిన': '2 Chronicles',
      'ఎజ్రా': 'Ezra',
      'నెహెమీయా': 'Nehemiah',
      'ఎస్తేరు': 'Esther',
      'యోబు': 'Job',
      'కీర్తనలు': 'Psalms',
      'సామెతలు': 'Proverbs',
      'ప్రసంగి': 'Ecclesiastes',
      'పరమగీతము': 'Song of Songs',
      'యెషయా': 'Isaiah',
      'యిర్మీయా': 'Jeremiah',
      'విలాపవాక్యములు': 'Lamentations',
      'యెహెఙ్కేలు': 'Ezekiel',
      'దానియేలు': 'Daniel',
      'దాని': 'Daniel',
      'హోషేయ': 'Hosea',
      'యోవేలు': 'Joel',
      'ఆమోసు': 'Amos',
      'ఒబద్యా': 'Obadiah',
      'యోనా': 'Jonah',
      'మీఖా': 'Micah',
      'నహూము': 'Nahum',
      'హబకూకు': 'Habakkuk',
      'జెఫన్యా': 'Zephaniah',
      'హగయి': 'Haggai',
      'జెకర్యా': 'Zechariah',
      'మలాకీ': 'Malachi',
      'మత్తయి': 'Matthew',
      'మార్కు': 'Mark',
      'లూకా': 'Luke',
      'యోహాను': 'John',
      'అపొ.కార్యములు': 'Acts',
      'అపొ': 'Acts',
      'అపొస్తలుల': 'Acts',
      'అపొస్తలుల కార్యములు': 'Acts',
      'అపొ కార్య': 'Acts',
      'అపొ కార్యములు': 'Acts',
      'రోమా': 'Romans',
      'రోమీయులకు': 'Romans',
      'రోమీయుల': 'Romans',
      '1కొరింథీయులకు': '1 Corinthians',
      'I కొరింథీయులకు': '1 Corinthians',
      'I కొరి': '1 Corinthians',
      '2కొరింథీయులకు': '2 Corinthians',
      'II కొరింథీయులకు': '2 Corinthians',
      'II కొరి': '2 Corinthians',
      'గలతీయులకు': 'Galatians',
      'ఎఫెసీయులకు': 'Ephesians',
      'ఫిలిప్పీయులకు': 'Philippians',
      'కొలొస్సయులకు': 'Colossians',
      '1థెసలొనీకయులకు': '1 Thessalonians',
      'I థెసలొనీకయులకు': '1 Thessalonians',
      'I థెస': '1 Thessalonians',
      '2థెసలొనీకయులకు': '2 Thessalonians',
      'II థెసలొనీకయులకు': '2 Thessalonians',
      'II థెస': '2 Thessalonians',
      '1తిమోతికి': '1 Timothy',
      'I తిమోతికి': '1 Timothy',
      'I తిమో': '1 Timothy',
      '2తిమోతికి': '2 Timothy',
      'II తిమోతికి': '2 Timothy',
      'II తిమో': '2 Timothy',
      'తీతుకు': 'Titus',
      'ఫిలేమోనుకు': 'Philemon',
      'హెబ్రీయులకు': 'Hebrews',
      'యాకోబు': 'James',
      '1పేతురు': '1 Peter',
      'I పేతురు': '1 Peter',
      'I పేతు': '1 Peter',
      '2పేతురు': '2 Peter',
      'II పేతురు': '2 Peter',
      'II పేతు': '2 Peter',
      '1యోహాను': '1 John',
      'I యోహాను': '1 John',
      'I యోహా': '1 John',
      '2యోహాను': '2 John',
      'II యోహాను': '2 John',
      'II యోహా': '2 John',
      '3యోహాను': '3 John',
      'III యోహాను': '3 John',
      'III యోహా': '3 John',
      'యూదా': 'Jude',
      'ప్రకటన': 'Revelation'
    };

    const result = bookMap[bookName] || null;
    console.log('బుక్ కీ రిజల్ట్:', result);
    return result;
  };

  return (
    <div className="card">
      <div id="word-details-top-ad" style={{ marginBottom: 16 }}></div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <button
          onClick={onBack}
          style={{
            padding: '8px 16px',
            fontSize: 16,
            borderRadius: '8px',
            border: '1px solid #bdbdbd',
            background: '#eeeeee',
            color: '#212121',
            cursor: 'pointer',
            outline: 'none',
          }}
        >వెనక్కి</button>
        <button
          onClick={() => toggleBookmark(entry.word)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 24,
            color: isBookmarked ? '#ffc107' : 'transparent',
            WebkitTextStroke: isBookmarked ? 'none' : '1px black',
            outline: 'none',
            transition: 'all 0.2s ease',
          }}
        >
          ⭐
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <h2 style={{ color: '#2d4373', fontWeight: 700, fontSize: 28, marginTop: 0 }}>{entry.word}</h2>
        {isBookmarked && (
          <span style={{ color: '#4CAF50', fontSize: 24 }}>✓</span>
        )}
      </div>
      <p style={{ fontSize: 20, color: '#6b5e3c', margin: '16px 0' }}>{entry.meaning}</p>

      {entry.refference && (
        <div style={{ marginTop: 16, fontSize: 16 }}>
          {entry.refference.split(';').map((ref, index, array) => {
            const trimmedRef = ref.trim();
            return (
              <React.Fragment key={index}>
                <span
                  onClick={() => handleReferenceClick(trimmedRef)}
                  style={{
                    color: '#bfae82',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    display: 'inline-block',
                    padding: '4px 8px',
                    margin: '0 2px',
                    borderRadius: '4px',
                    backgroundColor: 'rgba(191, 174, 130, 0.1)',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    msUserSelect: 'none',
                  }}
                >
                  {trimmedRef}
                </span>
                {index < array.length - 1 && '; '}
              </React.Fragment>
            );
          })}
        </div>
      )}
      {selectedVerses.length > 0 && (
        <div style={{ marginTop: 16 }}>
          {selectedVerses.map((verse, index) => (
            <div 
              key={index}
              style={{ 
                padding: 16,
                backgroundColor: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                marginBottom: index < selectedVerses.length - 1 ? 16 : 0,
              }}
            >
              <h3 style={{ 
                color: 'var(--text-color)', 
                marginTop: 0,
                marginBottom: 12,
                fontSize: 18,
                fontWeight: 600,
              }}>
                {verse.book} {verse.chapter}:{verse.verse}
              </h3>
              <p 
                style={{ 
                  color: 'var(--text-color)',
                  fontSize: 16,
                  lineHeight: 1.6,
                  margin: 0,
                }}
                dangerouslySetInnerHTML={{ __html: verse.text }}
              />
            </div>
          ))}
        </div>
      )}

      <div id="word-details-bottom-ad" style={{ marginTop: 16 }}></div>
    </div>
  );
};

// Component for History page
const HistoryPage: React.FC<{ history: string[], onBack: () => void, onWordClick: (word: string) => void, onClear: () => void }> = ({ history, onBack, onWordClick, onClear }) => {
  return (
    <div className="card" style={{ 
      backgroundColor: 'var(--card-bg)',
      borderColor: 'var(--border-color)',
      color: 'var(--text-color)'
    }}>
      <div id="history-top-ad" style={{ marginBottom: 16 }}></div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <button onClick={onBack} style={{ marginBottom: 0 }}>వెనక్కి</button>
        {history.length > 0 && (
          <button 
            onClick={onClear}
            style={{
              padding: '8px 16px',
              fontSize: 16,
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: 'var(--card-bg)',
              color: 'var(--text-color)',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            చరిత్రను తొలగించండి
          </button>
        )}
      </div>
      <h2>చరిత్ర</h2>
      {history.length === 0 ? (
        <p>చరిత్ర లేదు.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {history.map((word, index) => (
            <React.Fragment key={index}>
              <li 
                onClick={() => onWordClick(word)} 
                style={{ 
                  cursor: 'pointer', 
                  padding: '8px 0', 
                  borderBottom: '1px solid var(--border-color)',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'var(--hover-color)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
              >
                {word}
              </li>
              {(index + 1) % 7 === 0 && index !== history.length - 1 && (
                <li style={{ padding: '8px 0' }}>
                  <div id={`history-list-ad-${index}`}></div>
                </li>
              )}
            </React.Fragment>
          ))}
        </ul>
      )}
    </div>
  );
};

// Component for Bookmarks page
const BookmarksPage: React.FC<{ bookmarks: string[], data: DictionaryEntry[], onBack: () => void, onWordClick: (word: string) => void, onClear: () => void }> = ({ bookmarks, data, onBack, onWordClick, onClear }) => {
   const bookmarkedEntries = bookmarks.map(word => data.find(entry => entry.word === word)).filter(entry => entry !== undefined) as DictionaryEntry[];

  return (
    <div className="card" style={{ 
      backgroundColor: 'var(--card-bg)',
      borderColor: 'var(--border-color)',
      color: 'var(--text-color)'
    }}>
      <div id="bookmarks-top-ad" style={{ marginBottom: 16 }}></div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <button onClick={onBack} style={{ marginBottom: 0 }}>వెనక్కి</button>
        {bookmarks.length > 0 && (
          <button 
            onClick={onClear}
            style={{
              padding: '8px 16px',
              fontSize: 16,
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: 'var(--card-bg)',
              color: 'var(--text-color)',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            బుక్‌మార్క్‌లను తొలగించండి
          </button>
        )}
      </div>
      <h2>బుక్‌మార్క్‌లు</h2>
      {bookmarkedEntries.length === 0 ? (
        <p>బుక్‌మార్క్‌లు లేవు.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {bookmarkedEntries.map((entry, index) => (
            <React.Fragment key={index}>
              <li 
                onClick={() => onWordClick(entry.word)} 
                style={{ 
                  cursor: 'pointer', 
                  padding: '8px 0', 
                  borderBottom: '1px solid var(--border-color)',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'var(--hover-color)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
              >
                {entry.word}
              </li>
              {(index + 1) % 7 === 0 && index !== bookmarkedEntries.length - 1 && (
                <li style={{ padding: '8px 0' }}>
                  <div id={`bookmarks-list-ad-${index}`}></div>
                </li>
              )}
            </React.Fragment>
          ))}
        </ul>
      )}
    </div>
  );
};

// Component for Settings page
const SettingsPage: React.FC<{ onBack: () => void, isDarkTheme: boolean, setTheme: (dark: boolean) => void }> = ({ onBack, isDarkTheme, setTheme }) => {
  return (
    <div className="card" style={{ 
      backgroundColor: 'var(--card-bg)',
      borderColor: 'var(--border-color)',
      color: 'var(--text-color)'
    }}>
      <button onClick={onBack} style={{ 
        marginBottom: 16,
        padding: '8px 16px',
        fontSize: 16,
        borderRadius: '8px',
        border: '1px solid var(--border-color)',
        background: '#ffffff',
        color: '#000000',
        cursor: 'pointer',
        outline: 'none'
      }}>వెనక్కి</button>
      <h2>సెట్టింగ్స్</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
        <span style={{ fontSize: '18px', color: 'var(--text-color)' }}>థీమ్:</span>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setTheme(false)}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: !isDarkTheme ? '#ffffff' : 'var(--bg-color)',
              color: !isDarkTheme ? '#000000' : 'var(--text-color)',
              cursor: 'pointer',
              outline: 'none',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontSize: '16px'
            }}
          >
            ☀️ లైట్
          </button>
          <button
            onClick={() => setTheme(true)}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: isDarkTheme ? '#2d2d2d' : 'var(--bg-color)',
              color: isDarkTheme ? '#ffffff' : 'var(--text-color)',
              cursor: 'pointer',
              outline: 'none',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontSize: '16px'
            }}
          >
            🌙 డార్క్
          </button>
        </div>
      </div>
    </div>
  );
};

const TELUGU_ALPHABETS = [
  'అ', 'ఆ', 'ఇ', 'ఈ', 'ఉ', 'ఊ', 'ఋ', 'ఎ', 'ఏ', 'ఐ', 'ఒ', 'ఓ', 'ఔ',
  'క', 'ఖ', 'గ', 'ఘ', 'ఙ',
  'చ', 'ఛ', 'జ', 'ఝ', 'ఞ',
  'ట', 'ఠ', 'డ', 'ఢ', 'ణ',
  'త', 'థ', 'ద', 'ధ', 'న',
  'ప', 'ఫ', 'బ', 'భ', 'మ',
  'య', 'ర', 'ల', 'వ', 'శ', 'ష', 'స', 'హ', 'ళ', 'క్ష', 'ఱ'
];

function App() {
  const [data, setData] = useState<DictionaryEntry[]>(() => {
    const cachedData = localStorage.getItem('dictionaryData');
    if (cachedData) {
      return JSON.parse(cachedData);
    }
    return [];
  });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<DictionaryEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLetter, setSelectedLetter] = useState<string>('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const [isWebView, setIsWebView] = useState(false);
  const [history, setHistory] = useState<string[]>(() => {
    const savedHistory = localStorage.getItem('searchHistory');
    return savedHistory ? JSON.parse(savedHistory) : [];
  });
  const [bookmarks, setBookmarks] = useState<string[]>(() => {
    const savedBookmarks = localStorage.getItem('bookmarks');
    return savedBookmarks ? JSON.parse(savedBookmarks) : [];
  });
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [bibleData, setBibleData] = useState<{ [key: string]: any }>(() => {
    const cachedBibleData = localStorage.getItem('bibleData');
    if (cachedBibleData) {
      return JSON.parse(cachedBibleData);
    }
    return {};
  });
  const navigate = useNavigate();
  const location = useLocation();

  const setTheme = (dark: boolean) => {
    setIsDarkTheme(dark);
  };

  useEffect(() => {
    const root = document.documentElement;
    if (isDarkTheme) {
      root.style.setProperty('--bg-color', '#1a1a1a');
      root.style.setProperty('--text-color', '#ffffff');
      root.style.setProperty('--card-bg', '#2d2d2d');
      root.style.setProperty('--border-color', '#404040');
      root.style.setProperty('--hover-color', '#404040');
      root.style.setProperty('--header-color', '#bfae82');
      root.style.setProperty('--input-bg', '#2d2d2d');
      root.style.setProperty('--input-border', '#404040');
      root.style.setProperty('--mobile-nav-bg', '#2d4373');
      root.style.setProperty('--mobile-nav-border', '#2d4373');
      root.style.setProperty('--mobile-nav-text', '#ffffff');
      root.style.setProperty('--mobile-nav-active', '#ffc107');
      root.style.setProperty('--mobile-nav-inactive', '#bfae82');
    } else {
      root.style.setProperty('--bg-color', '#ffffff');
      root.style.setProperty('--text-color', '#000000');
      root.style.setProperty('--card-bg', '#f5f5f5');
      root.style.setProperty('--border-color', '#e3eaff');
      root.style.setProperty('--hover-color', '#e3eaff');
      root.style.setProperty('--header-color', '#bfae82');
      root.style.setProperty('--input-bg', '#ffffff');
      root.style.setProperty('--input-border', '#e3eaff');
      root.style.setProperty('--mobile-nav-bg', '#2d4373');
      root.style.setProperty('--mobile-nav-border', '#2d4373');
      root.style.setProperty('--mobile-nav-text', '#ffffff');
      root.style.setProperty('--mobile-nav-active', '#ffc107');
      root.style.setProperty('--mobile-nav-inactive', '#bfae82');
    }
  }, [isDarkTheme]);

  useEffect(() => {
    if (data.length > 0) {
      setLoading(false);
      return;
    }

    fetch('dictionary.json')
      .then((res) => res.json())
      .then((json) => {
        const dictObj = json.dictionary ? json.dictionary : json;
        const newData = Object.values(dictObj) as DictionaryEntry[];
        setData(newData);
        localStorage.setItem('dictionaryData', JSON.stringify(newData));
        setLoading(false);
      })
      .catch(() => {
        setError('డేటా లోడ్ కాలేదు.');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (Object.keys(bibleData).length > 0) {
      return;
    }

    console.log('బైబిల్ డేటా లోడ్ చేయడం ప్రారంభం...');
    
    const loadBibleData = async () => {
      try {
        const books = [
          'Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy',
          'Joshua', 'Judges', 'Ruth', '1 Samuel', '2 Samuel',
          '1 Kings', '2 Kings', '1 Chronicles', '2 Chronicles',
          'Ezra', 'Nehemiah', 'Esther', 'Job', 'Psalms', 'Proverbs',
          'Ecclesiastes', 'Song of Songs', 'Isaiah', 'Jeremiah',
          'Lamentations', 'Ezekiel', 'Daniel', 'Hosea', 'Joel',
          'Amos', 'Obadiah', 'Jonah', 'Micah', 'Nahum', 'Habakkuk',
          'Zephaniah', 'Haggai', 'Zechariah', 'Malachi', 'Matthew',
          'Mark', 'Luke', 'John', 'Acts', 'Romans', '1 Corinthians',
          '2 Corinthians', 'Galatians', 'Ephesians', 'Philippians',
          'Colossians', '1 Thessalonians', '2 Thessalonians',
          '1 Timothy', '2 Timothy', 'Titus', 'Philemon', 'Hebrews',
          'James', '1 Peter', '2 Peter', '1 John', '2 John',
          '3 John', 'Jude', 'Revelation'
        ];

        const loadedData: { [key: string]: any } = {};
        
        for (const book of books) {
          const response = await fetch(`https://raw.githubusercontent.com/aruljohn/Bible-telugu/main/${book}.json`);
          if (!response.ok) {
            console.error(`${book} లోడ్ కాలేదు:`, response.status);
            continue;
          }
          const data = await response.json();
          loadedData[book] = data;
          console.log(`${book} లోడ్ అయింది`);
        }

        console.log('అన్ని బుక్‌లు లోడ్ అయ్యాయి:', loadedData);
        setBibleData(loadedData);
        localStorage.setItem('bibleData', JSON.stringify(loadedData));
      } catch (error) {
        console.error('బైబిల్ డేటా లోడ్ కాలేదు:', error);
      }
    };

    loadBibleData();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const availableAlphabets = React.useMemo(() => {
    const set = new Set<string>();
    data.forEach(entry => {
      const first = entry.word[0];
      if (TELUGU_ALPHABETS.includes(first)) {
        set.add(first);
      }
    });
    return TELUGU_ALPHABETS.filter(l => set.has(l));
  }, [data]);

  const filtered = data.filter((entry: DictionaryEntry) => {
    const matchesLetter = selectedLetter
      ? entry.word.startsWith(selectedLetter)
      : true;
    const matchesSearch = entry.word.includes(search.trim());
    return matchesLetter && matchesSearch;
  });

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleLetterClick = (letter: string) => {
    setSelectedLetter(letter === selectedLetter ? '' : letter);
    setSelected(null);
    if (isMobileView) {
        navigate('/');
    }
  };

  const handleWordClick = (entry: DictionaryEntry) => {
    if (isMobileView) {
      if (!history.includes(entry.word)) {
        setHistory([...history, entry.word]);
      }
      navigate(`/word/${entry.word}`);
    } else {
      setSelected(entry);
    }
  };

  const handleListWordClick = (word: string) => {
     if (isMobileView) {
        navigate(`/word/${word}`);
     }
  };

  const toggleBookmark = (word: string) => {
    if (bookmarks.includes(word)) {
      setBookmarks(bookmarks.filter(b => b !== word));
    } else {
      setBookmarks([...bookmarks, word]);
    }
  };

  React.useEffect(() => {
    if (selectedLetter && !availableAlphabets.includes(selectedLetter)) {
      setSelectedLetter('');
    }
  }, [availableAlphabets, selectedLetter]);

  useEffect(() => {
    localStorage.setItem('searchHistory', JSON.stringify(history));
  }, [history]);

  useEffect(() => {
    localStorage.setItem('bookmarks', JSON.stringify(bookmarks));
  }, [bookmarks]);

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem('searchHistory');
  };

  const clearBookmarks = () => {
    setBookmarks([]);
    localStorage.removeItem('bookmarks');
  };

  const toggleSideMenu = () => {
    setIsSideMenuOpen(!isSideMenuOpen);
  };

  const closeSideMenu = () => {
    setIsSideMenuOpen(false);
  };

  const clearCache = () => {
    localStorage.removeItem('dictionaryData');
    localStorage.removeItem('bibleData');
    window.location.reload();
  };

  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isInWebView = userAgent.includes('wv') || // Android WebView
                       userAgent.includes('webview') || // iOS WebView
                       userAgent.includes('mobile safari') && !userAgent.includes('chrome'); // iOS Safari
    setIsWebView(isInWebView);
  }, []);

  return (
    <>
      <div style={{
        fontFamily: 'Noto Sans Telugu, sans-serif',
        padding: 24,
        maxWidth: 900,
        margin: '0 auto',
        backgroundColor: 'var(--bg-color)',
        color: 'var(--text-color)',
        minHeight: '100vh',
        position: 'relative'
      }}>
        {/* Header Section */}
        <div className="header-container">
          <span style={{ marginRight: 12 }}>
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="6" width="32" height="28" rx="6" fill={isDarkTheme ? '#2d2d2d' : '#fffbe8'} stroke={isDarkTheme ? '#404040' : '#e3eaff'} strokeWidth="2"/>
              <rect x="8" y="10" width="24" height="20" rx="3" fill={isDarkTheme ? '#404040' : '#e3eaff'} />
              <rect x="18" y="10" width="4" height="20" fill={isDarkTheme ? '#2d2d2d' : '#f7f5e6'} />
              <rect x="19.5" y="14" width="1" height="12" fill="#bfae82" />
              <rect x="16" y="19.5" width="8" height="1" fill="#bfae82" />
            </svg>
          </span>
          <h1 style={{ textAlign: 'center', color: 'var(--header-color)', fontWeight: 700, fontSize: 32, margin: 0, letterSpacing: 1 }}>తెలుగు బైబిల్ నిఘంటువు</h1>
        </div>

        {/* Main Content Area - Using Routes */}
        <div className="main-content-area">
          <Routes>
            {/* Main list view route */}
            <Route path="/" element={
              <>
                {!isWebView && (
                  <>
                    {/* Search input and button container */}
                    <div className="search-container" style={{ display: 'flex', marginBottom: 18 }}>
                      <input
                        type="text"
                        placeholder="పదాన్ని వెతకండి..."
                        value={search}
                        onChange={handleSearch}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            setSubmittedSearch(search);
                          }
                        }}
                        style={{
                          flex: 1,
                          padding: 12,
                          fontSize: 20,
                          borderRadius: isMobileView ? '20px' : '8px',
                          border: `1px solid var(--input-border)`,
                          background: 'var(--input-bg)',
                          color: 'var(--text-color)',
                          outline: 'none',
                          boxShadow: '0 1px 6px 0 rgba(191,174,130,0.04)',
                        }}
                      />
                    </div>

                    <div className="main-layout">
                      {/* Vertical Alphabet Bar */}
                      <div className="alphabet-bar" style={{
                        backgroundColor: 'var(--card-bg)',
                        borderColor: 'var(--border-color)'
                      }}>
                        <button
                          onClick={() => handleLetterClick('')}
                          className={selectedLetter === '' ? 'alphabet-btn selected' : 'alphabet-btn'}
                          style={{
                            color: 'var(--text-color)',
                            backgroundColor: selectedLetter === '' ? 'var(--hover-color)' : 'transparent'
                          }}
                        >ALL</button>
                        {availableAlphabets.map((letter: string) => (
                          <button
                            key={letter}
                            onClick={() => handleLetterClick(letter)}
                            className={selectedLetter === letter ? 'alphabet-btn selected' : 'alphabet-btn'}
                            style={{
                              color: 'var(--text-color)',
                              backgroundColor: selectedLetter === letter ? 'var(--hover-color)' : 'transparent'
                            }}
                          >
                            {letter}
                          </button>
                        ))}
                      </div>

                      {/* Word List */}
                      <div className="word-list-col">
                        {loading && <div style={{ textAlign: 'center', color: 'var(--header-color)' }}>లోడవుతోంది...</div>}
                        {error && <div style={{ color: 'red', textAlign: 'center' }}>{error}</div>}
                        {!loading && !error && (
                          <div className="word-list mobile-word-list" style={{
                            minWidth: 120,
                            backgroundColor: 'var(--card-bg)',
                            borderColor: 'var(--border-color)'
                          }}>
                            {filtered.length === 0 && <div style={{ color: 'var(--header-color)', textAlign: 'center' }}>పదాలు లేవు.</div>}
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                              {filtered.map((entry: DictionaryEntry) => (
                                <li
                                  key={entry.word}
                                  style={{
                                    padding: '10px 0',
                                    cursor: 'pointer',
                                    background: !isMobileView && selected?.word === entry.word ? 'var(--hover-color)' : 'transparent',
                                    borderBottom: `1px solid var(--border-color)`,
                                    color: 'var(--text-color)',
                                    fontWeight: !isMobileView && selected?.word === entry.word ? 700 : 400,
                                    fontSize: 18,
                                    transition: 'background 0.2s',
                                  }}
                                  onClick={() => handleWordClick(entry)}
                                >
                                  {entry.word}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* Word Details */}
                      {!isMobileView && (
                        <div className="details-col">
                          {selected ? (
                            <div className="card" style={{
                              backgroundColor: 'var(--card-bg)',
                              borderColor: 'var(--border-color)'
                            }}>
                              <h2 style={{ color: 'var(--text-color)', fontWeight: 700, fontSize: 28, marginTop: 0 }}>{selected.word}</h2>
                              <p style={{ fontSize: 20, color: 'var(--text-color)', margin: '16px 0' }}>{selected.meaning}</p>
                              {selected.refference && (
                                <div style={{ color: 'var(--header-color)', marginTop: 16, fontSize: 16 }}>{selected.refference}</div>
                              )}
                            </div>
                          ) : (
                            <div className="card" style={{
                              color: 'var(--header-color)',
                              textAlign: 'center',
                              fontSize: 18,
                              backgroundColor: 'var(--card-bg)',
                              borderColor: 'var(--border-color)'
                            }}>పదాన్ని ఎంచుకోండి.</div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {isWebView && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '20px',
                    color: 'var(--header-color)',
                    fontSize: '18px'
                  }}>
                    తెలుగు బైబిల్ నిఘంటువు
                  </div>
                )}
              </>
            } />

            {/* Route for Word Details page in mobile - Only shown on Mobile */}
            {isMobileView && (
              <Route
                path="/word/:word"
                element={
                  <WordDetailsPage data={data} onBack={() => navigate(-1)} bookmarks={bookmarks} toggleBookmark={toggleBookmark} />
                }
              />
            )}

            {/* Route for History page - Only shown on Mobile */}
            {isMobileView && (
              <Route
                path="/history"
                element={
                  <HistoryPage 
                    history={history} 
                    onBack={() => navigate(-1)} 
                    onWordClick={handleListWordClick}
                    onClear={clearHistory}
                  />
                }
              />
            )}

            {/* Route for Bookmarks page - Only shown on Mobile */}
            {isMobileView && (
              <Route
                path="/bookmarks"
                element={
                  <BookmarksPage 
                    bookmarks={bookmarks} 
                    data={data} 
                    onBack={() => navigate(-1)} 
                    onWordClick={handleListWordClick}
                    onClear={clearBookmarks}
                  />
                }
              />
            )}

            {/* Route for Settings page - Only shown on Mobile */}
            {isMobileView && (
              <Route
                path="/settings"
                element={
                  <SettingsPage onBack={() => navigate(-1)} isDarkTheme={isDarkTheme} setTheme={setTheme} />
                }
              />
            )}
          </Routes>
        </div>

        {/* Mobile Bottom Navigation */}
        {isMobileView && (
          <nav style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: '#2d4373',
            borderTop: '1px solid #2d4373',
            display: 'flex',
            justifyContent: 'space-between',
            padding: '8px 16px',
            zIndex: 1000,
            WebkitTapHighlightColor: 'transparent'
          }}>
            {/* Menu Button */}
            <div
              onClick={toggleSideMenu}
              style={{
                color: '#bfae82',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: '4px',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none'
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <rect y="4" width="24" height="2" rx="1" fill="currentColor"/>
                <rect y="11" width="24" height="2" rx="1" fill="currentColor"/>
                <rect y="18" width="24" height="2" rx="1" fill="currentColor"/>
              </svg>
              <span style={{ fontSize: '12px' }}>Menu</span>
            </div>

            {/* Home Button */}
            <div 
              onClick={() => navigate('/')} 
              style={{
                color: location.pathname === '/' ? '#ffc107' : '#bfae82',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: '4px',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span style={{ fontSize: '12px' }}>Home</span>
            </div>

            {/* History Button */}
            <div 
              onClick={() => navigate('/history')} 
              style={{
                color: location.pathname === '/history' ? '#ffc107' : '#bfae82',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: '4px',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8v4l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <span style={{ fontSize: '12px' }}>History</span>
            </div>

            {/* Bookmarks Button */}
            <div 
              onClick={() => navigate('/bookmarks')} 
              style={{
                color: location.pathname === '/bookmarks' ? '#ffc107' : '#bfae82',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: '4px',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2v16z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span style={{ fontSize: '12px' }}>Bookmarks</span>
            </div>

            {/* Settings Button */}
            <div 
              onClick={() => navigate('/settings')} 
              style={{
                color: location.pathname === '/settings' ? '#ffc107' : '#bfae82',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: '4px',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span style={{ fontSize: '12px' }}>Settings</span>
            </div>
          </nav>
        )}

        {/* Footer */}          
        <div style={{ marginTop: 40, textAlign: 'center', color: '#bfae82', fontSize: 15, letterSpacing: 1 }}>
          <span style={{ verticalAlign: 'middle', marginRight: 6 }}>✝️</span>
          © {new Date().getFullYear()} Telugu Bible Dictionary
        </div>
      </div>

      {/* Side Menu Overlay and Drawer for Mobile */}
      {isMobileView && isSideMenuOpen && (
        <>
          {/* Overlay */}
          <div
            onClick={closeSideMenu}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.3)',
              zIndex: 1500,
              backdropFilter: 'blur(2px)'
            }}
          />
          {/* Drawer */}
          <div
            className="side-menu"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 0,
              width: 280,
              background: '#fffbe8',
              boxShadow: '0 0 20px rgba(0,0,0,0.1)',
              zIndex: 2000,
              padding: 0,
              transition: 'transform 0.3s ease',
              color: '#2d4373'
            }}
          >
            {/* Header */}
            <div style={{ 
              padding: '2px 20px 12px 20px', 
              borderBottom: '1px solid rgba(45, 67, 115, 0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
              marginBottom: '12px'
            }}>
              <h2 style={{ 
                margin: 0, 
                fontSize: 18, 
                color: '#2d4373',
                fontWeight: 600,
                position: 'absolute',
                left: 20
              }}>Menu</h2>
              <button
                onClick={closeSideMenu}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#2d4373',
                  padding: 4,
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s',
                  position: 'absolute',
                  right: 20
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(45, 67, 115, 0.1)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                aria-label="Close"
              >×</button>
            </div>

            {/* Menu Items */}
            <div style={{ 
              padding: '12px 0',
              display: 'flex', 
              flexDirection: 'column', 
              gap: 4 
            }}>
              <span 
                onClick={() => { navigate('/'); closeSideMenu(); }} 
                style={{ 
                  cursor: 'pointer', 
                  fontSize: 16,
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 20px',
                  color: location.pathname === '/' ? '#2d4373' : '#666',
                  fontWeight: location.pathname === '/' ? 600 : 400,
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(45, 67, 115, 0.05)';
                  e.currentTarget.style.color = '#2d4373';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = location.pathname === '/' ? '#2d4373' : '#666';
                }}
              >
                {location.pathname === '/' && (
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    background: '#2d4373',
                    borderRadius: '0 2px 2px 0'
                  }} />
                )}
                Home
              </span>
              <span 
                onClick={() => { navigate('/history'); closeSideMenu(); }} 
                style={{ 
                  cursor: 'pointer', 
                  fontSize: 16,
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 20px',
                  color: location.pathname === '/history' ? '#2d4373' : '#666',
                  fontWeight: location.pathname === '/history' ? 600 : 400,
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(45, 67, 115, 0.05)';
                  e.currentTarget.style.color = '#2d4373';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = location.pathname === '/history' ? '#2d4373' : '#666';
                }}
              >
                {location.pathname === '/history' && (
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    background: '#2d4373',
                    borderRadius: '0 2px 2px 0'
                  }} />
                )}
                History
              </span>
              <span 
                onClick={() => { navigate('/bookmarks'); closeSideMenu(); }} 
                style={{ 
                  cursor: 'pointer', 
                  fontSize: 16,
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 20px',
                  color: location.pathname === '/bookmarks' ? '#2d4373' : '#666',
                  fontWeight: location.pathname === '/bookmarks' ? 600 : 400,
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(45, 67, 115, 0.05)';
                  e.currentTarget.style.color = '#2d4373';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = location.pathname === '/bookmarks' ? '#2d4373' : '#666';
                }}
              >
                {location.pathname === '/bookmarks' && (
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    background: '#2d4373',
                    borderRadius: '0 2px 2px 0'
                  }} />
                )}
                Bookmarks
              </span>
              <span 
                onClick={() => { navigate('/settings'); closeSideMenu(); }} 
                style={{ 
                  cursor: 'pointer', 
                  fontSize: 16,
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 20px',
                  color: location.pathname === '/settings' ? '#2d4373' : '#666',
                  fontWeight: location.pathname === '/settings' ? 600 : 400,
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(45, 67, 115, 0.05)';
                  e.currentTarget.style.color = '#2d4373';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = location.pathname === '/settings' ? '#2d4373' : '#666';
                }}
              >
                {location.pathname === '/settings' && (
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    background: '#2d4373',
                    borderRadius: '0 2px 2px 0'
                  }} />
                )}
                Settings
              </span>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default App;